// components/StickyTOC.tsx
// Auto-extracts H2 headings from markdown, highlights the active section on scroll.
// Desktop: fixed sidebar. Mobile: floating button with bottom drawer.

import React, { useState, useEffect, useRef } from 'react';
import { List, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle } from './ui/sheet';
import { cn } from '../lib/utils';

interface TOCItem {
  id: string;
  text: string;
  level: number;
}

interface StickyTOCProps {
  content: string;
}

const slugify = (text: string): string =>
  text
    .toLowerCase()
    .replace(/[^\w\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .trim();

const extractHeadings = (markdown: string): TOCItem[] => {
  const items: TOCItem[] = [];
  let inCodeBlock = false;

  for (const line of markdown.split('\n')) {
    if (line.trim().startsWith('```')) {
      inCodeBlock = !inCodeBlock;
      continue;
    }
    if (inCodeBlock) continue;

    const match = line.match(/^(#{1,3})\s+(.+)$/);
    if (match) {
      items.push({
        level: match[1].length,
        text: match[2].trim(),
        id: slugify(match[2].trim()),
      });
    }
  }
  return items;
};

export const StickyTOC: React.FC<StickyTOCProps> = ({ content }) => {
  const [headings, setHeadings] = useState<TOCItem[]>([]);
  const [activeId, setActiveId] = useState('');
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const observer = useRef<IntersectionObserver | null>(null);

  // Extract headings
  useEffect(() => {
    setHeadings(extractHeadings(content));
  }, [content]);

  // Observe headings in DOM
  useEffect(() => {
    if (headings.length === 0) return;

    observer.current = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter(e => e.isIntersecting);
        if (visible.length > 0) setActiveId(visible[0].target.id);
      },
      { rootMargin: '-96px 0px -60% 0px', threshold: 0.1 }
    );

    headings.forEach(({ id }) => {
      const el = document.getElementById(id);
      if (el) observer.current?.observe(el);
    });

    return () => observer.current?.disconnect();
  }, [headings]);

  const scrollTo = (id: string) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' });
      setActiveId(id);
      setMobileOpen(false);
    }
  };

  if (headings.length === 0) return null;

  // Show only H2s (H1 is page title, H3s are sub-details)
  const tocItems = headings.filter(h => h.level === 2);
  if (tocItems.length < 3) return null; // Not enough headings to justify a TOC

  const TOCLink = ({ item, className }: { item: TOCItem; className?: string }) => (
    <Button
      variant="ghost"
      onClick={() => scrollTo(item.id)}
      title={item.text}
      className={cn(
        'w-full justify-start text-left h-auto py-1.5 px-3 font-normal truncate',
        activeId === item.id
          ? 'bg-secondary text-primary font-semibold'
          : 'text-muted-foreground hover:text-foreground',
        className
      )}
    >
      {item.text}
    </Button>
  );

  return (
    <>
      {/* ---- DESKTOP: Fixed right sidebar ---- */}
      <nav
        className="hidden xl:block fixed top-32 right-8 w-64 max-h-[calc(100vh-10rem)] overflow-y-auto z-40"
        aria-label="Table of Contents"
      >
        <Card className="bg-background/80 backdrop-blur-sm shadow-sm py-0">
          <CardContent className="p-5">
            <Button
              variant="ghost"
              onClick={() => setCollapsed(!collapsed)}
              className="flex items-center justify-between w-full mb-3 h-auto p-0 font-mono uppercase tracking-widest text-[10px] text-muted-foreground hover:bg-transparent hover:text-foreground"
            >
              <span className="flex items-center gap-2">
                <List className="w-3 h-3" /> Contents
              </span>
              {collapsed
                ? <ChevronDown className="w-3 h-3" />
                : <ChevronUp className="w-3 h-3" />}
            </Button>

            {!collapsed && (
              <ul className="space-y-0.5">
                {tocItems.map((item) => (
                  <li key={item.id}>
                    <TOCLink item={item} className="text-[13px] leading-snug" />
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      </nav>

      {/* ---- MOBILE: FAB + Sheet drawer ---- */}
      <div className="xl:hidden fixed bottom-6 right-6 z-50">
        <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
          <SheetTrigger
            render={
              <Button
                size="icon"
                variant="primary"
                className="w-12 h-12 rounded-full shadow-lg"
              />
            }
          >
            <List className="w-5 h-5" />
            <span className="sr-only">Toggle table of contents</span>
          </SheetTrigger>
          <SheetContent side="bottom" className="max-h-[60vh] overflow-y-auto rounded-t-2xl">
            <SheetHeader>
              <SheetTitle className="font-mono uppercase tracking-widest text-[10px] text-muted-foreground">
                Contents
              </SheetTitle>
            </SheetHeader>
            <ul className="space-y-0.5 px-4 pb-4">
              {tocItems.map((item) => (
                <li key={item.id}>
                  <TOCLink item={item} className="text-sm py-2" />
                </li>
              ))}
            </ul>
          </SheetContent>
        </Sheet>
      </div>
    </>
  );
};